<script setup lang="ts">
import type { INote } from "~/models/IUserEntry";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

type Props = {
  activeNoteId: number | undefined;
  notesActiveParagraph: ILegalTextParagraph | undefined;
  notes: INote[];
};

type Emits = {
  close: [];
  setActive: [note: INote];
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const NoteEditor = defineAsyncComponent(
  () => import("~/components/legaltextparagraph/note/NoteEditor.vue")
);

const { t } = useI18n();

const { title } = useFullParagraphTitle(() => props.notesActiveParagraph, true);
</script>

<template>
  <div class="h-full space-y-4 overflow-y-auto py-2 pr-4">
    <div class="flex items-center justify-between">
      <h2 class="whitespace-nowrap font-bold">
        {{
          t(
            "notes_header",
            {
              paragraph: title,
            },
            9
          )
        }}
        <LawVersionIndicator :legalTextParagraph="notesActiveParagraph" />
      </h2>
      <button
        class="rounded-full p-2 transition hover:bg-gray-200"
        @click="emit('close')"
      >
        <span class="sr-only">{{ t("close") }}</span>
        <i-heroicons-x-mark-20-solid aria-hidden="true" class="size-5" />
      </button>
    </div>
    <NoteEditor
      v-for="note in notes"
      :key="note.id"
      class="scroll-my-2"
      :active="activeNoteId === note.id"
      :note="note"
      @click="emit('setActive', note)"
    />
  </div>
</template>

<i18n>
{
  "de": {
    "notes_header": "@:entries.note zu {paragraph}",
    "close": "@:entries.note schließen"
  },
  "en": {
    "notes_header": "@:entries.note to {paragraph}",
    "close": "close @:entries.note"
  }
}
</i18n>
